import type { ApiErrorBody } from "@xop-any-time/shared";

import { env } from "./env.js";

/**
 * Minimal fetch wrapper for the reseller BFF. Deliberately small — there is
 * no session/token to attach yet (auth is a stub, see
 * apps/server/src/routes/auth.ts), so this is just JSON in/out plus typed
 * error surfacing. Once real auth lands this is the place to add the
 * bearer/cookie handling xop-edge-staff's `lib/api.ts` does.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /**
   * Which fields the server refused and why, where it could say — see
   * `ApiErrorBody.fields`.
   *
   * A form reads this to mark the offending input rather than printing one
   * summary sentence above twenty-two fields, which is what Settings used to
   * do and what made a correct refusal read as a false one.
   */
  readonly fields: Record<string, string> | undefined;
  /**
   * The permission key crm-api refused, on a `reseller_permission_denied`
   * body (spec F §4.3, §6.3) — absent from every other error, and absent
   * even on that code when the BFF could not name one (see `crmShared.ts`'s
   * `upstreamError`). A fetch error carrying this is what a page renders a
   * `NoAccessPanel` for, in place of itself, via `isPermissionDenied` below.
   */
  readonly permission: string | undefined;
  /** Structured facts about the refusal — `ApiErrorBody.details`, e.g.
   * `{ member_count }` on a ROLE_IN_USE 409. */
  readonly details: Record<string, unknown> | undefined;

  constructor(
    message: string,
    status: number,
    code?: string,
    fields?: Record<string, string>,
    permission?: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.permission = permission;
    this.details = details;
  }
}

/**
 * True when `err` is a fetch failure the server refused for lacking a
 * specific permission — the shape a page's query/mutation error branch
 * checks before falling back to its generic error UI (spec F §6.3: "A 403
 * reseller_permission_denied from any fetch renders the same panel inline").
 *
 * `permission` may be absent (the BFF omits it when it cannot name one); a
 * caller that needs one falls back to the page's own view key, NOT to
 * "unknown" — `?? "unknown"` here is only for a caller with no better key to
 * offer.
 */
export function isPermissionDenied(err: unknown): err is ApiError & { permission: string } {
  return err instanceof ApiError && err.code === "reseller_permission_denied";
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  /** JSON-encoded and sent with `content-type: application/json`. */
  body?: unknown;
  /**
   * Sent verbatim, with whatever `content-type` the caller sets. For the
   * branding logo, which is raw image bytes — the BFF registers a body parser
   * per accepted image type and builds crm-api's multipart envelope on the
   * far side, so wrapping it here would mean two envelopes for one file.
   */
  rawBody?: ArrayBuffer;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, rawBody, headers, ...rest } = options;
  const init: RequestInit = {
    ...rest,
    headers: {
      ...(body !== undefined ? { "content-type": "application/json" } : {}),
      ...(headers ?? {}),
    },
    credentials: "include",
  };
  if (rawBody !== undefined) init.body = rawBody;
  else if (body !== undefined) init.body = JSON.stringify(body);

  const url = path.startsWith("http") ? path : `${env.apiUrl}${path}`;
  const res = await fetch(url, init);

  if (!res.ok) {
    let parsed: Partial<ApiErrorBody> & { permission?: string } = {};
    try {
      parsed = (await res.json()) as Partial<ApiErrorBody> & { permission?: string };
    } catch {
      // Non-JSON error body (e.g. a proxy 502) — fall through to a generic message.
    }
    throw new ApiError(
      parsed.message ?? `Request failed with status ${res.status}`,
      res.status,
      parsed.code,
      parsed.fields,
      parsed.permission,
      parsed.details,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
